// src/components/SPanel/Home/homeLessons.js
//
// Lecțiile elevului pentru pagina principală: numerotate în ordine, plus
// lecțiile încă neprogramate din pachet, ca drumul întreg să se vadă în
// carusel. Lecțiile anulate nu primesc număr (doar se numără). Instructorul
// nu apare pe pagina principală (se vede doar în detaliile lecției).

/** Câte lecții are pachetul (MAX_PACKAGE_LESSONS în backend). */
export const LESSON_PACKAGE = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(value) {
   if (!value) return null;
   const d = value instanceof Date ? value : new Date(value);
   return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Lista pentru carusel și cifrele pentru statistică.
 * `items[i].state`: done | live | scheduled | todo; `isNext` pe prima lecție
 * care n-a trecut încă (cea în desfășurare sau următoarea).
 */
export function buildLessonTrack(events, now = new Date()) {
   const nowMs = now.getTime();
   const lessons = [];
   let cancelled = 0;

   for (const event of events || []) {
      if (event?.kind !== "lesson") continue;
      const start = toDate(event.start);
      if (!start) continue;
      if (event.cardItem?.inactive) {
         cancelled += 1;
         continue;
      }
      const end = toDate(event.end) || new Date(start.getTime() + 90 * 60000);
      lessons.push({ event, start, end });
   }
   lessons.sort((a, b) => a.start - b.start);

   let drivenMinutes = 0;
   const items = lessons.map(({ event, start, end }, index) => {
      const state =
         end.getTime() <= nowMs
            ? "done"
            : start.getTime() <= nowMs
              ? "live"
              : "scheduled";
      if (state === "done") {
         drivenMinutes += Math.max(0, Math.round((end - start) / 60000));
      }
      return {
         key: `lesson-${event.id ?? start.getTime()}`,
         number: index + 1,
         state,
         isNext: false,
         start,
         end,
         plate: String(event.raw?.car?.plateNumber || "").trim(),
         gearbox: String(event.gearbox || event.raw?.gearbox || "").trim(),
         sector: String(event.sector || event.raw?.sector || "").trim(),
         event,
      };
   });

   const done = items.filter((item) => item.state === "done").length;
   const scheduled = items.length - done;
   const todo = Math.max(0, LESSON_PACKAGE - items.length);

   for (let n = items.length + 1; n <= LESSON_PACKAGE; n += 1) {
      items.push({ key: `todo-${n}`, number: n, state: "todo", isNext: false });
   }

   let focusIndex = items.findIndex(
      (item) => item.state === "live" || item.state === "scheduled",
   );
   if (focusIndex >= 0) items[focusIndex].isNext = true;
   else focusIndex = items.findIndex((item) => item.state === "todo");
   if (focusIndex < 0) focusIndex = Math.max(0, items.length - 1);

   return {
      items,
      focusIndex,
      next: items.find((item) => item.isNext) || null,
      stats: {
         total: Math.max(LESSON_PACKAGE, lessons.length),
         done,
         scheduled,
         todo,
         cancelled,
         drivenMinutes,
      },
   };
}

/** „1 lecție”, „5 lecții”, „20 de lecții”. */
export function roCount(n, one, many) {
   if (n === 1) return `1 ${one}`;
   const rest = n % 100;
   const de = n > 0 && (rest === 0 || rest >= 20) ? " de" : "";
   return `${n}${de} ${many}`;
}

function startOfDay(date) {
   return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatTime(date) {
   return date.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
   });
}

export function formatDayMonth(date) {
   return {
      day: date.getDate(),
      month: date
         .toLocaleDateString("ro-RO", { month: "short" })
         .replaceAll(".", ""),
      weekday: date.toLocaleDateString("ro-RO", { weekday: "long" }),
   };
}

/** Cât mai e până la lecție: „acum”, „peste 40 min”, „mâine”, „peste 3 zile”. */
export function formatCountdown(start, end, now = new Date()) {
   const diff = start.getTime() - now.getTime();
   if (diff <= 0) return now < end ? "în desfășurare" : "acum";

   const minutes = Math.ceil(diff / 60000);
   if (minutes < 60) return `peste ${minutes} min`;

   const days = Math.round((startOfDay(start) - startOfDay(now)) / DAY_MS);
   if (days === 0) {
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;
      return rest ? `peste ${hours} h ${rest} min` : `peste ${hours} h`;
   }
   if (days === 1) return "mâine";
   if (days === 2) return "poimâine";
   return `peste ${roCount(days, "zi", "zile")}`;
}

/** „18 h 30 min” la volan. */
export function formatDriven(minutes) {
   const h = Math.floor(minutes / 60);
   const m = minutes % 60;
   if (!h) return `${m} min`;
   return m ? `${h} h ${m} min` : `${h} h`;
}
